import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Form, Dropdown, Card, ListGroup } from 'react-bootstrap';
import apiClient from '../api/axios';
import { toast } from 'react-toastify';
import ConfirmationModal from '../components/ConfirmationModal';
import DepartmentsManagerModal from '../components/DepartmentsManagerModal'; // Import the new modal
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

// Custom toggle for the actions dropdown
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="btn btn-link text-dark p-3 border-0"
    >
      {children}
    </button>
));

const EventRolesPage = () => {
    const { user } = useAuth();
    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Form state
    const [newRoleName, setNewRoleName] = useState('');
    const [newRoleDepartment, setNewRoleDepartment] = useState('');

    const { canCreate, canUpdate, canDelete, loading: permissionsLoading } = usePermissions('eventRole');
    const { canCreate: canCreateDepartment } = usePermissions('department');

    // Modals state
    const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Editing state
    const [editingRole, setEditingRole] = useState(null);
    const [editingRoleName, setEditingRoleName] = useState('');
    const [editingRoleDepartment, setEditingRoleDepartment] = useState('');

    // Delete confirmation state
    const [deleteModalContent, setDeleteModalContent] = useState({ title: '', message: '', onConfirm: null });

    const refreshRoles = useCallback(() => {
        apiClient.get('/event-roles/')
            .then(response => setRoles(response.data))
            .catch(err => toast.error('Не удалось обновить список ролей.'));
    }, []);

    const refreshDepartments = useCallback(() => {
        if (!user?.organization_id) return;
        apiClient.get(`/departments/organization/${user.organization_id}`)
            .then(response => setDepartments(response.data))
            .catch(err => toast.error('Не удалось обновить список направлений.'));
    }, [user?.organization_id]);

    useEffect(() => {
        if (user?.organization_id) {
            setLoading(true);
            Promise.all([
                apiClient.get('/event-roles/'),
                apiClient.get(`/departments/organization/${user.organization_id}`)
            ]).then(([rolesResponse, departmentsResponse]) => {
                setRoles(rolesResponse.data);
                setDepartments(departmentsResponse.data);
            }).catch(err => {
                setError('Ошибка загрузки данных');
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [user?.organization_id]);

    const groupedRoles = useMemo(() => {
        if (!roles || !departments) return [];

        const departmentMap = new Map();

        departments.forEach(dept => {
            departmentMap.set(dept.id, {
                ...dept,
                roles: []
            });
        });

        roles.forEach(role => {
            if (role.department && departmentMap.has(role.department.id)) {
                departmentMap.get(role.department.id).roles.push(role);
            }
        });

        const sortedDepartments = Array.from(departmentMap.values())
            .sort((a, b) => a.name.localeCompare(b.name));

        sortedDepartments.forEach(dept => {
            dept.roles.sort((a, b) => a.name.localeCompare(b.name));
        });

        return sortedDepartments;
    }, [roles, departments]);

    const handleAddRole = (e) => {
        e.preventDefault();
        if (!newRoleName.trim() || !newRoleDepartment) {
            toast.error('Название роли и направление должны быть заполнены.');
            return;
        }

        apiClient.post('/event-roles/', { name: newRoleName.trim(), department_id: parseInt(newRoleDepartment) })
            .then(response => {
                refreshRoles();
                setNewRoleName('');
                setNewRoleDepartment('');
                toast.success('Роль успешно добавлена!');
            })
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Ошибка добавления роли');
            });
    };

    const handleEditClick = (role) => {
        setEditingRole(role);
        setEditingRoleName(role.name);
        setEditingRoleDepartment(role.department.id);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingRole(null);
        setEditingRoleName('');
        setEditingRoleDepartment('');
    };

    const handleUpdateRole = () => {
        if (!editingRole || !editingRoleDepartment) {
            toast.error('Направление должно быть выбрано.');
            return;
        }
        apiClient.put(`/event-roles/${editingRole.id}`, { name: editingRoleName, department_id: parseInt(editingRoleDepartment) })
            .then(() => {
                refreshRoles();
                handleCloseEditModal();
                toast.success('Роль успешно обновлена!');
            })
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Ошибка обновления роли');
            });
    };

    const handleDeleteClick = (role) => {
        setDeleteModalContent({
            title: 'Подтвердить удаление',
            message: `Вы уверены, что хотите удалить роль "${role.name}"?`,
            onConfirm: () => handleConfirmDelete(role.id),
        });
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = (roleId) => {
        apiClient.delete(`/event-roles/${roleId}`)
            .then(() => {
                refreshRoles();
                toast.success('Роль успешно удалена!');
                setShowDeleteModal(false);
            })
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Ошибка удаления роли');
                setShowDeleteModal(false);
            });
    };

    if (loading || permissionsLoading) return <p>Загрузка...</p>;
    if (error) return <p className="text-danger">{error}</p>;

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h2">Роли в событиях</h1>
                {canCreateDepartment && (
                    <Button variant="outline-secondary" onClick={() => setShowDepartmentsModal(true)}>
                        <i className="fas fa-sitemap me-2"></i>Направления
                    </Button>
                )}
            </div>
            
            {canCreate && (
                <div className="card mb-4">
                    <div className="card-header">Добавить новую роль</div>
                    <div className="card-body">
                        <Form onSubmit={handleAddRole}>
                            <div className="row">
                                <div className="col-md-6">
                                    <Form.Control 
                                        type="text" 
                                        value={newRoleName} 
                                        onChange={(e) => setNewRoleName(e.target.value)} 
                                        placeholder="Название новой роли"
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Form.Select
                                        value={newRoleDepartment}
                                        onChange={(e) => setNewRoleDepartment(e.target.value)}
                                        required
                                    >
                                        <option value="">Выберите направление</option>
                                        {departments.map(dept => (
                                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <div className="col-md-2">
                                    <Button type="submit" className="w-100">Добавить</Button>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div>
            )}

            {groupedRoles.length > 0 ? groupedRoles.map(group => (
                <Card key={group.id} className="mb-3">
                    <Card.Header as="h5">{group.name}</Card.Header>
                    <ListGroup variant="flush">
                        {group.roles.length > 0 ? group.roles.map(role => (
                            <ListGroup.Item key={role.id} className="d-flex justify-content-between align-items-center">
                                <span>{role.name}</span>
                                {(canUpdate || canDelete) && (
                                    <Dropdown>
                                        <Dropdown.Toggle as={CustomToggle} id={`dropdown-actions-${role.id}`}>
                                            <i className="fas fa-ellipsis-v"></i>
                                        </Dropdown.Toggle>
                                
                                        <Dropdown.Menu>
                                            {canUpdate && <Dropdown.Item onClick={() => handleEditClick(role)}>Изменить</Dropdown.Item>}
                                            {canDelete && <Dropdown.Item onClick={() => handleDeleteClick(role)} className="text-danger">Удалить</Dropdown.Item>}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                            </ListGroup.Item>
                        )) : (
                            <ListGroup.Item className="text-center text-muted fst-italic">В этом направлении пока нет ролей.</ListGroup.Item>
                        )}
                    </ListGroup>
                </Card>
            )) : (
                <div className="card">
                    <div className="card-body text-center">Ролей пока нет.</div>
                </div>
            )}

            {/* Edit Role Modal */}
            <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Редактировать роль</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={(e) => { e.preventDefault(); handleUpdateRole(); }}>
                        <Form.Group className="mb-3">
                            <Form.Label>Название роли</Form.Label>
                            <Form.Control
                                type="text"
                                value={editingRoleName}
                                onChange={(e) => setEditingRoleName(e.target.value)}
                                required
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Направление</Form.Label>
                            <Form.Select
                                value={editingRoleDepartment}
                                onChange={(e) => setEditingRoleDepartment(e.target.value)}
                                required
                            >
                                {departments.map(dept => (
                                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditModal}>Отмена</Button>
                    <Button variant="primary" onClick={handleUpdateRole}>Сохранить</Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal 
                show={showDeleteModal}
                title={deleteModalContent.title}
                message={deleteModalContent.message}
                onConfirm={deleteModalContent.onConfirm}
                onCancel={() => setShowDeleteModal(false)}
                confirmText="Удалить"
                cancelText="Отмена"
            />

            {/* Departments Management Modal */}
            <DepartmentsManagerModal 
                show={showDepartmentsModal} 
                handleClose={() => setShowDepartmentsModal(false)}
                onUpdate={refreshDepartments} 
            />
        </>
    );
};

export default EventRolesPage;